import { api, DATA_BASE } from '../../services/apiClient';

// La foto del profesor se sirve desde staff_profile (endpoint propio de profesores, NO el de alumnos).
export function teacherPhotoUrl(id: number) { return `${DATA_BASE}/teachers/${id}/photo`; }

export interface TeacherRow {
  account_id: number; firstname: string; lastname: string;
  email: string | null; phone_prefix: string | null; phone_number: string | null;
  has_photo: number; classes: number; students: number;
  /** 1 = sigue en el estudio; 0 = dado de baja (solo llega con getTeachers({ inactivos: true })). */
  active?: number;
  /** Solo con getTeachers({ equipo: true }): staff que no da clases, con su rol y puesto («Cajera», «Gerente», «Dueño(a)»). */
  role?: string;
  puesto?: string | null;
  es_profesor?: number;
  /** Esquema de pago (mismo juego de campos que la ficha): por hora, con tarifa de mañana, o comisión. */
  pay_mode?: string | null;
  hourly_rate?: string | number | null;
  morning_rate?: string | number | null;
  morning_until?: string | null;
  commission_pct?: string | number | null;
}
export interface TeacherClass {
  class_id: number; name: string; color: string | null; capacity: number | null;
  style: string | null; enrolled: number;
  schedule: { weekday: number; start_time: string; end_time: string }[];
}
// Última clase DADA = la lista de asistencia más reciente de sus clases (prueba de que ocurrió);
// llega de Kydemy en el delta diario, así que la de hoy puede aparecer hasta mañana.
export interface TeacherLastClass {
  date: string; class_id: number; name: string; color: string | null;
  start_time: string | null; total: number; present: number;
}
// Siguiente clase = próxima ocurrencia del HORARIO de sus clases vivas (calculada en el backend).
export interface TeacherNextClass {
  date: string; weekday: number; start_time: string; end_time: string | null;
  class_id: number; name: string; color: string | null;
}
export interface TeacherDetail {
  account_id: number; firstname: string; lastname: string; username: string | null;
  email: string | null; phone_prefix: string | null; phone_number: string | null;
  dob: string | null; notes: string | null; active: number;
  has_photo: number; has_password: number; students: number; classes: TeacherClass[];
  last_class?: TeacherLastClass | null; next_class?: TeacherNextClass | null;
  /** 0 = STAFF que no da clases (gerencia, caja, marketing, dueñas): misma ficha, sin clases ni esquema de pago. */
  es_profesor?: number;
  role?: string;
  /** Puesto del staff (misma detección que Usuarios); null para profesores. */
  puesto?: string | null;
  /** Tarifa por hora (letsmove_teacher_rates); null si no se le ha puesto. */
  hourly_rate?: string | number | null;
  /** Cómo cobra: 'hourly' (por hora) o 'commission' (% de lo que pagan sus alumnas). */
  pay_mode?: string | null;
  /** Porcentaje de comisión cuando pay_mode = 'commission'. */
  commission_pct?: string | number | null;
  /** Tarifa distinta para clases que empiezan antes de morning_until. */
  morning_rate?: string | number | null;
  morning_until?: string | null;
}

export interface TeacherInput {
  firstname: string; lastname?: string; username?: string; email?: string | null;
  phone_prefix?: string | null; phone_number?: string | null; dob?: string | null; notes?: string | null;
}

/** `inactivos` (Mabel 1-oct-2026): incluye a los dados de baja para consultarlos o reactivarlos.
 *  `equipo` (Mabel 3-oct-2026): en vez de profesores, el STAFF que no da clases (con su puesto). */
export function getTeachers(opts?: { inactivos?: boolean; equipo?: boolean }) {
  const q = [opts?.inactivos ? 'inactivos=1' : '', opts?.equipo ? 'equipo=1' : ''].filter(Boolean).join('&');
  return api.get<{ data: TeacherRow[] }>(`/teachers${q ? `?${q}` : ''}`);
}
export function getTeacher(id: number) { return api.get<{ data: TeacherDetail }>(`/teachers/${id}`); }
/** Lo que el maestro ha pagado (vitrina y abonos a sus fiados). */
export interface TeacherPayment {
  payment_id: number; pay_date: string | null; datetime: string; type: string; method: string; amount: string;
  assoc_name: string | null; comment: string | null; debt_id: number | null; qty: number | null; teller: string | null;
}
export function getTeacherPayments(id: number) { return api.get<{ data: TeacherPayment[]; total: number; count: number }>(`/teachers/${id}/payments`); }
export function createTeacher(data: TeacherInput) { return api.post<{ data: { account_id: number; username: string } }>('/teachers', data); }
export function updateTeacher(id: number, data: Partial<TeacherInput> & { active?: number }) { return api.put<{ ok: true }>(`/teachers/${id}`, data); }

// Asignar/quitar clases DESDE la ficha del profesor (video Karla 30-jul). replace=true solo
// tras confirmar: la clase ya tenía otro maestro y se le pisa (el backend responde 409 sin él).
export function assignTeacherClass(id: number, classId: number, replace = false) {
  return api.post<{ ok: true; already?: boolean }>(`/teachers/${id}/classes`, { class_id: classId, replace });
}
export function unassignTeacherClass(id: number, classId: number) {
  return api.del<{ ok: true }>(`/teachers/${id}/classes/${classId}`);
}

// ASISTENCIA de la persona (Mabel 19-oct-2026): pestaña «Asistencia» de la ficha. Solo lectura.
// Las sesiones salen de la MISMA cuenta que Finanzas › Nómina (horario × días + «no se dio»/«suplente»).
export type AsistEstado = 'vino' | 'no_vino' | 'suplente' | 'suplio';
export interface AsistSesion {
  date: string; class_id: number; class_name: string; start_time: string | null; hours: number;
  scheduled: boolean; has_list: boolean; estado: AsistEstado;
  substitute_name: string | null; substitute_for: string | null; marked_by: string | null; note: string | null; futura: boolean;
}
export interface AsistAjuste {
  id: number; period_from: string; period_to: string; concept: 'falta' | 'permiso' | 'retardo'; concept_label: string;
  nombre: string; note: string | null; created_at: string | null; created_by_name: string | null;
}
export interface TeacherAttendanceData {
  user: { id: number; name: string; es_profesor: number }; from: string; to: string; hoy: string;
  resumen: { programadas: number; vino: number; no_vino: number; con_suplente: number; sin_lista: number;
    fuera_de_horario: number; suplio: number; horas: number; proximas: number };
  sesiones: AsistSesion[]; ajustes: AsistAjuste[]; ve_ajustes: boolean;
}
/** `to` exclusivo (como la nómina). */
export function getTeacherAttendance(id: number, from: string, to: string) {
  return api.get<{ data: TeacherAttendanceData }>(`/teachers/${id}/attendance?from=${from}&to=${to}`);
}

// FALTAS / PERMISOS / RETARDOS desde el perfil (Mabel 19-oct-2026). SOLO REGISTRO: no descuenta de la nómina.
export type IncidKind = 'falta' | 'permiso' | 'retardo';
export interface Incidencia {
  id: number; user_id: number; date: string; kind: IncidKind; kind_label: string; minutes: number | null; note: string | null;
  created_at: string | null; created_by_name: string | null; updated_at: string | null; updated_by_name: string | null;
}
export interface IncidenciasData {
  disponible: boolean; from: string; to: string; incidencias: Incidencia[];
  resumen: { faltas: number; permisos: number; retardos: number; minutos: number };
  can_edit: boolean; motivo_no_edita: string | null; es_profesor: number;
}
export interface IncidenciaInput { date: string; kind: IncidKind; minutes?: number | null; note?: string | null }
/** `to` exclusivo. */
export function getIncidencias(id: number, from: string, to: string) {
  return api.get<{ data: IncidenciasData }>(`/teachers/${id}/incidents?from=${from}&to=${to}`);
}
export function createIncidencia(id: number, body: IncidenciaInput) { return api.post<{ ok: true; incidencia: Incidencia }>(`/teachers/${id}/incidents`, body); }
export function updateIncidencia(id: number, incId: number, body: Partial<IncidenciaInput>) { return api.patch<{ ok: true; incidencia: Incidencia }>(`/teachers/${id}/incidents/${incId}`, body); }
export function deleteIncidencia(id: number, incId: number) { return api.del<{ ok: true }>(`/teachers/${id}/incidents/${incId}`); }
